import React from 'react'

import { Alert, Tag, Tooltip, Typography } from 'antd'

import { observer } from 'mobx-react-lite'

import { FlexBoxY } from 'common/components/boxes'
import { OrderMaterialSpreadsheetFooter } from 'common/components/OrderMaterialsSpreadsheet'
import { OrderMaterials } from 'common/components/OrderMaterialsV2'
import { OrderMaterialTabs } from 'common/components/OrderMaterialTabs'
import { formatDateString, formatEnumValue } from 'common/helpers/formatters'
import { OrderStates } from 'common/server/server_types'
import { Unit } from 'common/server/units'

import { Order } from 'vendor/server/orders'

import { DeliveryHeader, DeliveryHeaderProps } from './delivery_header'

const allowedOrderStatesToEditUnitCost = [OrderStates.QUOTED]

type MaterialsAndDeliveriesProps = {
  order: Order
  onSetOrderDirty: (isDirty: boolean) => void
  deliveryTotalCost: (deliverId: string) => number
  materialsCount: (deliverId: string) => number
  getOrderMaterialsByDeliveryId: (deliveryId: string) => Order['order_materials']
  updateOrderMaterialsByDeliveryId: (deliveryId: string, orderMaterials: Order['order_materials']) => void
  units?: Unit[]
  isPoLocked?: boolean
} & Pick<DeliveryHeaderProps, 'addExistingFiles' | 'fileList' | 'updateDelivery'>

export const MaterialsAndDeliveries = observer<MaterialsAndDeliveriesProps>((props) => {
  const {
    units = [],
    order,
    onSetOrderDirty,
    updateDelivery,
    addExistingFiles,
    fileList,
    deliveryTotalCost,
    getOrderMaterialsByDeliveryId,
    updateOrderMaterialsByDeliveryId,
    isPoLocked,
  } = props

  const { deliveries = [] } = order

  const onChangeDelivery = (index, params: { [key: string]: string | number }) => {
    deliveries[index] = { ...deliveries[index], ...params }
    onSetOrderDirty(true)
  }

  const orderMaterialsTotalCost = (deliveryId: string) => {
    const orderMaterials = getOrderMaterialsByDeliveryId(deliveryId) || []
    return orderMaterials.reduce((acc, orderMaterial) => acc + Number(orderMaterial.ext_cost), 0)
  }

  const allowedEditUnitCost = !order.vendor_price_lock_enabled || allowedOrderStatesToEditUnitCost.includes(order.state)

  return (
    <OrderMaterialTabs
      hideAdd
      disabled={isPoLocked}
      items={deliveries?.map((delivery, index) => {
        const idx = index + 1

        const deliveryIssueType = formatEnumValue(delivery?.delivery_issue_type || '')

        return {
          label: (
            <OrderMaterialTabs.TabPaneTitle
              isPickUp={delivery.is_pick_up}
              title={
                <FlexBoxY justifyContent="flex-start" alignItems="flex-start">
                  <Typography.Text type="secondary">Requested Date</Typography.Text>
                  <Typography.Text>{formatDateString(delivery.requested_delivered_at, 'Unknown')}</Typography.Text>

                  {delivery?.has_open_issue && (
                    <Tooltip title={delivery?.delivery_issues}>
                      <Tag color={'red'} style={{ margin: 0 }}>
                        {deliveryIssueType}
                      </Tag>
                    </Tooltip>
                  )}
                </FlexBoxY>
              }
            />
          ),
          key: `${index}`,
          closable: false,
          children: (
            <>
              {delivery?.has_open_issue && (
                <Alert
                  closable
                  message={`${deliveryIssueType}: ${delivery?.delivery_issues || ''}`}
                  type="error"
                  style={{ marginBottom: 12 }}
                />
              )}

              <DeliveryHeader
                delivery={delivery}
                order={order}
                onChange={(params) => onChangeDelivery(index, params)}
                updateDelivery={updateDelivery}
                addExistingFiles={addExistingFiles}
                fileList={fileList}
                disabled={isPoLocked}
              />

              <OrderMaterials
                isVendorView
                dataSource={getOrderMaterialsByDeliveryId(delivery.id)}
                onChange={(newOrderMaterials) => {
                  updateOrderMaterialsByDeliveryId(delivery.id, newOrderMaterials)
                  onSetOrderDirty(true)
                }}
                hideTax
                hideCostCode
                hideAddNewItem
                showDeliveryIssues
                deliveryId={delivery.id}
                unitInput={{
                  units,
                  edit: false,
                }}
                disabled={isPoLocked}
                unitCostInput={{
                  hideDetails: true,
                  edit: allowedEditUnitCost,
                }}
                quantityInput={{ edit: false }}
                vendorResponseInput={{ allowedEditUnitCost }}
                editCompanyNote={false}
              />

              <OrderMaterialSpreadsheetFooter
                index={idx}
                disabled
                isPoLocked={isPoLocked}
                deliveryChargesUnits={delivery.delivery_charges_units}
                onChangeChargesUnit={(value) => (delivery.delivery_charges_units = value)}
                deliveryTotalCost={deliveryTotalCost(delivery.id)}
                onChange={(value) => {
                  delivery.discount_value = value.discountValue
                  delivery.shipping_value = value.shippingValue
                  delivery.other_value = value.otherValue
                  delivery.tax_value = value.taxValue
                  onSetOrderDirty(true)
                }}
                orderMaterialsTotalCost={orderMaterialsTotalCost(delivery?.id)}
                roundingPrecision={3}
                value={{
                  discountValue: delivery?.discount_value,
                  shippingValue: delivery?.shipping_value,
                  otherValue: delivery?.other_value,
                  taxValue: delivery?.tax_value,
                }}
                inputsProps={{
                  tax_value: { disabled: order.project?.tax_line_items?.enabled },
                }}
              />
            </>
          ),
        }
      })}
    />
  )
})
